<script setup lang="ts">
import type {
  ClassicPortraitGalleryFilter,
  MixedPortraitGalleryKind,
} from './classic-mixed-portrait-gallery';

interface Props {
  counts: Record<MixedPortraitGalleryKind, number>;
  modelValue: ClassicPortraitGalleryFilter;
}

interface Tab {
  count: number;
  key: string;
  value: ClassicPortraitGalleryFilter;
}

const props = defineProps<Props>();
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<{
  'update:modelValue': [value: ClassicPortraitGalleryFilter];
}>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const tabs: Tab[] = [
  {
    count: props.counts.group + props.counts.individual,
    key: 'Portraits.filterAll',
    value: 'all',
  },
  {
    count: props.counts.group,
    key: 'Portraits.filterGroup',
    value: 'group',
  },
  {
    count: props.counts.individual,
    key: 'Portraits.filterIndividual',
    value: 'individual',
  },
];

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function countOf(tab: Tab): number {
  if (tab.value === 'all') {
    return props.counts.group + props.counts.individual;
  }
  return props.counts[tab.value];
}
</script>

<template>
  <div
    class="ClassicMixedPortraitGallery__filter"
    role="group"
    data-testid="classic-mixed-gallery-filter"
    :aria-label="$t('Portraits.filterLabel')"
  >
    <button
      v-for="tab in tabs"
      :key="tab.value"
      class="ClassicMixedPortraitGallery__filter-tab"
      :class="{
        'ClassicMixedPortraitGallery__filter-tab--active': tab.value === modelValue,
      }"
      type="button"
      :data-testid="`classic-mixed-gallery-filter-${tab.value}`"
      :aria-pressed="tab.value === modelValue"
      @click="emit('update:modelValue', tab.value)"
    >
      {{ $t(tab.key) }}
      <span class="ClassicMixedPortraitGallery__filter-count">{{ countOf(tab) }}</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
.ClassicMixedPortraitGallery__filter {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  justify-self: center;
  padding: 0.25rem;
  background: rgba(var(--Kiosk--tint-color-rgb), 0.12);
  border-radius: 99rem;
}

.ClassicMixedPortraitGallery__filter-tab {
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
  min-height: var(--cmg-touch);
  padding: 0 1.4rem;
  font-family: var(--Kiosk--font_regular_url);
  font-size: 1.3rem;
  line-height: 1;
  color: var(--Kiosk--font_color);
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
  background: transparent;
  border: 0;
  border-radius: 99rem;
  outline: none;
  transition:
    background-color 140ms ease,
    color 140ms ease;
}

.ClassicMixedPortraitGallery__filter-tab:focus-visible {
  box-shadow: inset 0 0 0 0.2rem var(--Kiosk--button_background_color);
}

.ClassicMixedPortraitGallery__filter-tab--active {
  color: var(--Kiosk--button_font_color);
  background: var(--Kiosk--button_background_color);
}

.ClassicMixedPortraitGallery__filter-count {
  font-variant-numeric: tabular-nums;
  opacity: 0.72;
}
</style>
